import { CampaignDeletionModal } from '@/components/CampaignDeletionModal';
import { ColorInput } from '@/components/ColorInput';
import { GenericSelect } from '@/components/GenericSelect';
import Loader from '@/components/Loader';
import { SegmentationModal, SelectedLocation } from '@/components/SegmentationModal';
import { SelectMediaCard } from '@/components/SelectMediaCard';
import { SortableMediaCard } from '@/components/SortableMediaCard';
import { VideoModal } from '@/components/VideoModal';
import BaseLayout from '@/layout/BaseLayout';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { useCampaignFormStore } from '@/screens/CampaignEdit/store';
import { trpc, trpcClient } from '@/trpcClient';
import { campaignDisplayFormatMapper } from '@/utils/mapper';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { EDisplayFormat } from '@shelflix/schemas/_common/enum';
import { allowedColors } from '@shelflix/schemas/_common/validation';
import { checkCampaignEditInput, type EditCampaignInputType } from '@shelflix/schemas/campaign/edit';
import type { BrandDto } from '@shelflix/trpc/routes/brand/list/useCase';
import type { CampaignMediaDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import type { MediaDto } from '@shelflix/trpc/routes/media/list/useCase';
import { DatePicker } from '@shelflix/ui-web/components/DatePicker/index';
import Dropzone, { CropArea } from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Label } from '@shelflix/ui-web/components/ui/label';
import { ScrollArea, ScrollBar } from '@shelflix/ui-web/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Textarea } from '@shelflix/ui-web/components/ui/textarea';
import { useMutation, useQuery } from '@tanstack/react-query';
import { addDays, subDays } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Função utilitária para converter CampaignMediaDto em MediaDto
function campaignMediaToMediaDto(media: CampaignMediaDto): MediaDto {
  return {
    uuid: media.uuid,
    name: media.name,
    url: media.mediaUrl,
    thumbnailUrl: media.thumbnailUrl || '',
    duration: media.duration,
    active: true,
    extension: media.extension,
    displayFormat: media.displayFormat || EDisplayFormat.PORTRAIT, // Fallback para PORTRAIT se displayFormat estiver ausente
  };
}

export function CampaignEdit() {
  const { uuid: campaignUuid } = useParams<{ uuid: string }>();

  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formData, resetForm } = useCampaignFormStore();
  const [brand, setBrand] = useState<BrandDto | null>(null);
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const [availableMedias, setAvailableMedias] = useState<CampaignMediaDto[]>([]);
  const [selectedMedias, setSelectedMedias] = useState<CampaignMediaDto[]>([]);
  const [mediatoCampaign, setMediatoCampaign] = useState<CampaignMediaDto[]>([]);

  // Adicionar estados para thumbnail
  const [thumbnail, setThumbnail] = useState<File | string | null>(null);
  const [crop, setCrop] = useState<CropArea | undefined>(undefined);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();

  const [isLoadingData, setIsLoadingData] = useState(true);

  const form = useForm<
    EditCampaignInputType & {
      brand: {
        uuid: string;
        name: string;
        ledColor: string;
      };
    }
  >({
    defaultValues: formData,
    mode: 'all',
  });

  const { data: campaignToEdit, isLoading: _isLoadingMedia } = useQuery({
    ...trpc.campaign.get.queryOptions(
      {
        uuid: campaignUuid ?? '',
      },
      {
        enabled: !!campaignUuid,
      }
    ),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const fetchCampaignData = async () => {
      if (campaignToEdit && campaignToEdit.uuid && campaignToEdit.medias) {
        form.reset({
          uuid: campaignToEdit.uuid,
          name: campaignToEdit.name,
          displayFormat: campaignToEdit.displayFormat,
          startDate: campaignToEdit.startDate,
          endDate: campaignToEdit.endDate ?? undefined,
          description: campaignToEdit.description ?? undefined,
          brand: campaignToEdit.brand,
          medias: campaignToEdit.medias.map((media) => media.uuid),
        });

        if (campaignToEdit.thumbnailUrl) {
          setThumbnail(campaignToEdit.thumbnailUrl);
        }

        const brand = await trpcClient.brand.get.query({
          uuid: campaignToEdit.brand.uuid,
        });

        setBrand({
          uuid: brand.uuid,
          activeCampaigns: 0,
          active: brand.active,
          category: brand.category,
          code: brand.code,
          ledColor: brand.ledColor,
          logoUrl: brand.logoUrl,
          mediaCount: 0,
          name: brand.name,
          relatedBrands: brand.relatedBrands,
        });

        // Corrigir setMediatoCampaign para não misturar File no thumbnailUrl
        setMediatoCampaign(
          campaignToEdit.medias.map((media) => {
            return {
              uuid: media.uuid,
              name: media.name,
              duration: media.duration,
              thumbnailUrl: media.thumbnailUrl || '',
              mediaUrl: media.mediaUrl,
              extension: media.extension,
            };
          })
        );

        const locations: SelectedLocation[] = [];
        for (const seg of campaignToEdit.segmentation) {
          const { count } = await trpcClient.material.getCount.query({
            cityOrStateIbgeCode: seg.ibgeCode,
          });

          const newLocation: SelectedLocation = {
            uuid: seg.uuid,
            name: seg.name,
            isState: seg.isState,
            materialCount: Number(count ?? 0),
          };

          locations.push(newLocation);
        }
        setSelectedLocation(locations);

        setIsLoadingData(false);
      }
    };

    fetchCampaignData();
  }, [campaignToEdit]);

  const editCompaignMutation = useMutation(trpc.campaign.edit.mutationOptions());
  const deleteCampaignMutation = useMutation(trpc.campaign.delete.mutationOptions());
  const updateAndDeleteMutation = useMutation(trpc.campaign.updateDefaultAndDelete.mutationOptions());

  // State for available campaigns (for batch update)
  const [availableCampaigns, setAvailableCampaigns] = useState<Array<{ uuid: string; name: string }>>([]);
  const [isLoadingAvailableCampaigns, setIsLoadingAvailableCampaigns] = useState(false);

  const [deletionModalData, setDeletionModalData] = useState<{
    isOpen: boolean;
    canDelete: boolean;
    dependencies?: {
      hasDefaultMaterialDependency: boolean;
      hasCurrentMaterialDependency: boolean;
      defaultMaterials: Array<{ uuid: string; name: string }>;
      currentMaterials: Array<{ uuid: string; name: string }>;
    };
  }>({
    isOpen: false,
    canDelete: true,
  });

  const handleDeleteCampaign = () => {
    if (!campaignUuid) return;

    // Show confirmation modal first (campaign deletion happens on confirm)
    setDeletionModalData({
      isOpen: true,
      canDelete: true,
    });
  };

  // Fetch available campaigns when warning modal opens (for batch update)
  useEffect(() => {
    if (deletionModalData.isOpen && !deletionModalData.canDelete && campaignToEdit?.brand?.uuid) {
      setIsLoadingAvailableCampaigns(true);
      trpcClient.campaign.list
        .query({
          brandUuid: campaignToEdit.brand.uuid,
          recordsPerPage: 100, // Get first 100 campaigns
        })
        .then((result) => {
          // Filter out the campaign being deleted
          const filtered = result.campaigns
            .filter((c) => c.uuid !== campaignUuid)
            .map((c) => ({ uuid: c.uuid, name: c.name }));
          setAvailableCampaigns(filtered);
        })
        .catch((error) => {
          toast.error(`Erro ao carregar campanhas: ${error.message}`);
          setAvailableCampaigns([]);
        })
        .finally(() => {
          setIsLoadingAvailableCampaigns(false);
        });
    }
  }, [deletionModalData.isOpen, deletionModalData.canDelete, campaignToEdit?.brand?.uuid, campaignUuid]);

  // Handle batch update and delete
  const handleUpdateAndDelete = (newDefaultCampaignUuid: string) => {
    if (!campaignUuid || !newDefaultCampaignUuid) return;

    updateAndDeleteMutation.mutate(
      {
        campaignToDeleteUuid: campaignUuid,
        newDefaultCampaignUuid,
      },
      {
        onSuccess: () => {
          toast.success('Materiais atualizados e campanha excluída com sucesso');
          navigate('/campaign');
        },
        onError: (error) => {
          toast.error(`Erro ao atualizar e excluir: ${error.message}`);
        },
      }
    );
  };

  const confirmDeletion = () => {
    if (!campaignUuid) return;

    deleteCampaignMutation.mutate(
      { uuid: campaignUuid },
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success('Campaign deleted successfully');
            navigate('/campaign');
          } else if (response.dependencies) {
            // Campaign has dependencies - show warning modal with dependencies
            setDeletionModalData({
              isOpen: true,
              canDelete: false,
              dependencies: response.dependencies,
            });
          }
        },
        onError: (error) => {
          toast.error(`Failed to delete campaign: ${error.message}`);
          setDeletionModalData({ isOpen: false, canDelete: true });
        },
      }
    );
  };

  const onSubmit = async (
    data: EditCampaignInputType & {
      brand: {
        uuid: string;
        name: string;
      };
    }
  ) => {
    if (editCompaignMutation.isPending || uploadImageMutation.isPending) return;
    if (thumbnailRemoved && !(thumbnail instanceof File)) {
      toast.error('Selecione uma nova thumbnail antes de salvar.');
      return;
    }
    setIsSubmitting(true);

    if (campaignUuid) {
      try {
        setIsUploading(true);

        // First, upload the thumbnail with compression if there's a new one
        let thumbnailKey: string | undefined;
        if (thumbnail instanceof File) {
          const base64Data = await fileToBase64(thumbnail);
          const uploadResult = await uploadImageMutation.mutateAsync({
            base64Data,
            filename: thumbnail.name,
            imageType: 'campaign-thumbnail',
            cropOptions: crop,
          });
          thumbnailKey = uploadResult.key;
          logger.info('Thumbnail uploaded', {
            originalSize: uploadResult.originalSize,
            compressedSize: uploadResult.compressedSize,
            savingsPercent: uploadResult.savingsPercent,
          });
        }

        // Only send fields expected by the schema
        const endDateValue = !data.endDate || data.endDate?.trim() === '' ? undefined : data.endDate;

        const payload: EditCampaignInputType = {
          uuid: campaignUuid,
          name: data.name,
          displayFormat: data.displayFormat,
          startDate: data.startDate,
          description: data.description ?? undefined,
          medias: mediatoCampaign.map((media) => media.uuid),
          brand: typeof data.brand === 'string' ? data.brand : (data.brand as { uuid: string }).uuid,
          segmentation: selectedLocation.map((location) => {
            return {
              uuid: location.uuid,
              name: location.name,
              isState: location.isState,
            };
          }),
          ...(endDateValue && { endDate: endDateValue }),
          ...(thumbnailKey && { thumbnailKey }), // Use pre-uploaded key if available
          ...(thumbnailRemoved && !thumbnail && { thumbnailFileType: 'REMOVE' }),
        };

        await editCompaignMutation.mutateAsync(payload);
        resetForm();
        navigate('/campaign', { state: { toastSuccess: true } });
      } catch (error) {
        toast.error(`Erro ao editar campanha: ${(error as Error).message}`);
      } finally {
        setIsUploading(false);
        setIsSubmitting(false);
      }
    }
  };

  const { data, isLoading, isError } = useQuery(
    trpc.media.list.queryOptions(
      {
        brandUuid: form.getValues('brand')?.uuid,
        recordsPerPage: 999,
      },
      { enabled: !!form.getValues('brand')?.uuid }
    )
  );

  const medias = data?.medias ?? [];

  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');
  const displayFormat = form.watch('displayFormat');

  // Filter available medias by the selected displayFormat (orientation)
  useEffect(() => {
    if (!isLoading && !isError) {
      const filteredMedias = displayFormat ? medias.filter((media) => media.displayFormat === displayFormat) : medias;
      setAvailableMedias(
        filteredMedias.map((media) => ({
          uuid: media.uuid,
          name: media.name,
          duration: media.duration,
          mediaUrl: media.url ?? '',
          thumbnailUrl: media.thumbnailUrl ?? '',
          extension: media.extension,
          displayFormat: media.displayFormat,
        }))
      );
      // Clear selected medias that don't match the new displayFormat
      setSelectedMedias((prev) => prev.filter((m) => !displayFormat || m.displayFormat === displayFormat));
    }
  }, [data?.medias, isLoading, isError, displayFormat]);

  const onToggleMedia = (media: CampaignMediaDto, checked: boolean) => {
    setSelectedMedias((prev) => {
      if (checked) {
        if (!prev.some((m) => m.uuid === media.uuid)) {
          return [...prev, media];
        }
        return prev;
      } else {
        return prev.filter((m) => m.uuid !== media.uuid);
      }
    });
  };

  const handleAddMediaToCampaign = () => {
    setMediatoCampaign((prev) => {
      const newMedias = selectedMedias.filter((media) => !prev.some((m) => m.uuid === media.uuid));
      return [...prev, ...newMedias];
    });

    setSelectedMedias([]);
  };

  const handleRemoveMediaFromCampaign = (media: CampaignMediaDto) => {
    setMediatoCampaign((prev) => prev.filter((m) => m.uuid !== media.uuid));
  };

  const handleMoveMedia = (index: number, direction: 'left' | 'right') => {
    setMediatoCampaign((prev) => {
      const newOrder = [...prev];
      const targetIndex = direction === 'left' ? index - 1 : index + 1;

      if (targetIndex < 0 || targetIndex >= newOrder.length) return prev;

      [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];

      return newOrder;
    });
  };

  const removeLocation = (location: SelectedLocation) => {
    setSelectedLocation((prev) => prev.filter((loc) => loc.uuid !== location.uuid));
  };

  const migrateCampaignMutation = useMutation(trpc.migration.campaignMigration.mutationOptions());

  return (
    <BaseLayout>
      <div className="px-2 md:px-0">
        {isLoadingData || isLoading || _isLoadingMedia ? (
          <Loader message="Carregando campanha..." />
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="flex w-full flex-col gap-8">
              <div className="flex flex-col flex-wrap gap-4 sm:flex-row">
                <Card className="flex flex-1 rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary leading-5 font-semibold sm:text-2xl">
                      Informações de campanha
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      rules={{ validate: (value) => checkCampaignEditInput('name', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">Nome da Campanha</FormLabel>
                          <FormControl>
                            <Input className=" " placeholder="Digite o nome da campanha" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="brand"
                      rules={{ validate: (value) => checkCampaignEditInput('brand', value?.uuid ?? value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">Marca</FormLabel>
                          <FormControl>
                            <GenericSelect<BrandDto>
                              onSelect={(brand) => {
                                field.onChange(brand.uuid ?? brand);
                                setBrand(brand);
                              }}
                              value={field.value}
                              fetchItems={async (search) => {
                                // Fetch directly via trpcClient to avoid stale data from useQuery cache
                                const result = await trpcClient.brand.list.query({
                                  ...(search && {
                                    search: {
                                      fields: ['name', 'code'],
                                      value: search,
                                    },
                                  }),
                                  recordsPerPage: 9999,
                                });

                                return (result.brands ?? []).sort((a, b) => a.name.localeCompare(b.name));
                              }}
                              renderItem={(brand) => (
                                <div>
                                  <div className="text-sm font-medium">{brand.name}</div>
                                  <div className="text-muted-foreground text-xs">{brand.category}</div>
                                </div>
                              )}
                              getItemLabel={(brand) => brand.name}
                              getItemKey={(brand) => brand.uuid}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="text-xs font-medium text-(--gray-primary)">
                      <Label className="pb-1 text-xs font-medium text-(--gray-primary)">Código de cor - LED</Label>
                      <ColorInput value={brand?.ledColor ?? '#fff'} disabled mode="select" options={allowedColors} />
                    </div>
                    <FormField
                      control={form.control}
                      name="displayFormat"
                      rules={{ validate: (value) => checkCampaignEditInput('displayFormat', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">
                            Formato de exibição
                          </FormLabel>
                          <FormControl>
                            <Select
                              value={field.value ?? ''}
                              onValueChange={(displayFormat: EDisplayFormat) => {
                                field.onChange(displayFormat);
                              }}
                            >
                              <SelectTrigger className="h-10 w-full">
                                <SelectValue placeholder="Selecione uma opção" />
                              </SelectTrigger>
                              <SelectContent className=" ">
                                {Object.values(EDisplayFormat).map((displayFormat) => (
                                  <SelectItem key={displayFormat} value={displayFormat}>
                                    {campaignDisplayFormatMapper(displayFormat)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="startDate"
                      rules={{ validate: (value) => checkCampaignEditInput('startDate', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">Data de Início</FormLabel>
                          <FormControl>
                            <DatePicker
                              mode="single"
                              onChange={(value) =>
                                field.onChange(value instanceof Date ? value?.toISOString() : undefined)
                              }
                              value={field.value ? new Date(field.value) : undefined}
                              toDate={endDate ? subDays(new Date(endDate), 1) : undefined}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endDate"
                      rules={{
                        validate: (value) =>
                          checkCampaignEditInput('endDate', !value || value?.trim?.() === '' ? undefined : value),
                      }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">
                            Data de Encerramento (não obrigatório)
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              mode="single"
                              onChange={(value) =>
                                field.onChange(value instanceof Date ? (value?.toISOString() ?? undefined) : undefined)
                              }
                              value={field.value ? new Date(field.value) : undefined}
                              fromDate={startDate ? addDays(new Date(startDate), 1) : undefined}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                  <CardContent className="mt-4 flex flex-1">
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem className="flex w-full flex-1 flex-col gap-0.75">
                          <FormLabel className="text-xs font-medium text-(--gray-primary)">Descrição</FormLabel>
                          <FormControl>
                            <Textarea
                              className="min-h-32"
                              rows={5}
                              onInput={field.onChange}
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                <Card className="rounded-[10px]">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-primary text-xl leading-5 font-semibold md:text-2xl">
                      Thumbnail
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {thumbnail && typeof thumbnail === 'string' ? (
                      <div className="flex flex-col gap-4">
                        <div style={{ maxWidth: 180 }} className="mx-auto flex aspect-9/16 items-center justify-center">
                          <img
                            src={thumbnail}
                            alt="Thumbnail atual"
                            className="h-full w-full rounded object-cover"
                            onError={() => {
                              setThumbnail('https://shelflix.com.br/wp-content/uploads/2025/02/Grey_Orange-300x90.png');
                            }}
                          />
                        </div>
                        <div className="flex justify-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setThumbnail(null);
                              setThumbnailRemoved(true);
                            }}
                            className="w-fit"
                          >
                            <Icon name="compare_arrows" />
                            Alterar thumbnail
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ maxWidth: 180 }} className="mx-auto aspect-9/16">
                        <Dropzone
                          removable={true}
                          onFilesSelected={(data) => {
                            setThumbnail(data[0]);
                            setThumbnailRemoved(false);
                          }}
                          title="Arraste ou clique aqui para selecionar a thumbnail"
                          description="Extensões permitidas: JPG, PNG. Proporção obrigatória: 9:16 (Máx. 5mb)"
                          value={typeof thumbnail === 'object' && thumbnail !== null ? [thumbnail as File] : undefined}
                          requiredAspectRatio="9:16"
                          className="h-full w-full"
                          readonly={false}
                          cropModal={true}
                          cropValue={crop}
                          onCropChange={(crop) => setCrop(crop)}
                          variant="thumb"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card className="rounded-[10px]">
                <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
                  <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                    Segmentações de conteúdo
                  </CardTitle>
                  <SegmentationModal
                    trigger={
                      <div className="flex w-full grow">
                        <Button type="button" className="ml-auto grow sm:grow-0" onClick={handleAddMediaToCampaign}>
                          <Icon name="device_hub" />
                          <span>Adicionar segmentação</span>
                        </Button>
                      </div>
                    }
                    onSaveSegmentation={(locations) => setSelectedLocation((prev) => [...(prev ?? []), ...locations])}
                  />
                </CardHeader>
                <CardContent className="mx-4 flex rounded-[10px] p-4">
                  <ScrollArea type="always" className="w-1 flex-1 flex-row flex-nowrap">
                    {selectedLocation.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedLocation.map((location) => (
                          <div
                            key={location.uuid}
                            className="text-primary flex w-81 items-start justify-between rounded-md border-2"
                          >
                            <div className="flex flex-col gap-2 p-2">
                              <span className="font-semibold">{location.name}</span>
                              <span className="flex flex-row items-center gap-2 font-['Figtree',Helvetica] font-normal text-(--input-color)">
                                <Icon name="devices" className="text-(--input-color)" size={16} />
                                {location.materialCount} Materiais
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              type="button"
                              className="px-2"
                              onClick={() => removeLocation(location)}
                            >
                              <Icon name="delete" className="text-primary" size={16} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card className="rounded-[10px]">
                <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
                  <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                    Mídias disponíveis
                  </CardTitle>
                  <div className="flex w-full grow">
                    <Button
                      type="button"
                      className="ml-auto grow sm:grow-0"
                      disabled={selectedMedias.length === 0}
                      onClick={handleAddMediaToCampaign}
                    >
                      <Icon name="add" />
                      <span>Adicionar na campanha</span>
                    </Button>
                  </div>
                </CardHeader>
                <section
                  className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
                  style={{ gap: isMobile ? '8px' : '28px' }}
                >
                  <Card className="grid min-h-32 w-full grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2 p-2 py-2 md:grid-cols-[repeat(auto-fill,minmax(134px,1fr))] md:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(144px,1fr))]">
                    {availableMedias.length > 0 &&
                      availableMedias
                        .filter((media) => !mediatoCampaign.some((includedMedia) => media.uuid === includedMedia.uuid))
                        .map((media) => (
                          <SelectMediaCard
                            key={media.uuid}
                            media={campaignMediaToMediaDto(media)}
                            brandName={brand?.name ?? ''}
                            isSelected={selectedMedias.some((m) => m.uuid === media.uuid)}
                            onToggle={(m, checked) => onToggleMedia(media, checked)}
                          />
                        ))}
                  </Card>
                </section>
              </Card>

              <Card className="rounded-[10px]">
                <CardHeader className="flex flex-col justify-between gap-4 pb-0 sm:flex-row">
                  <CardTitle className="text-primary shrink-0 text-[18px] leading-5 font-semibold sm:text-2xl">
                    Edição de campanha
                  </CardTitle>
                  <div className="flex w-full grow">
                    <VideoModal
                      videos={mediatoCampaign.map((media) => media.mediaUrl)}
                      trigger={
                        <div className="flex w-full grow">
                          <Button type="button" className="ml-auto grow sm:grow-0">
                            <Icon name="play_circle" />
                            <span>Assistir prévia</span>
                          </Button>
                        </div>
                      }
                    />
                  </div>
                </CardHeader>
                <section
                  className="flex w-full flex-wrap overflow-hidden rounded-[5px]"
                  style={{ gap: isMobile ? '8px' : '28px' }}
                >
                  <Card className="grid min-h-32 w-full grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2 p-2 py-2 md:grid-cols-[repeat(auto-fill,minmax(134px,1fr))] md:gap-4 lg:grid-cols-[repeat(auto-fill,minmax(144px,1fr))]">
                    {mediatoCampaign.length > 0 &&
                      mediatoCampaign.map((media, index) => (
                        <SortableMediaCard
                          key={media.uuid}
                          media={campaignMediaToMediaDto(media)}
                          onRemove={() => handleRemoveMediaFromCampaign(media)}
                          onMoveLeft={() => handleMoveMedia(index, 'left')}
                          onMoveRight={() => handleMoveMedia(index, 'right')}
                          disabledLeft={index === 0}
                          disabledRight={index === mediatoCampaign.length - 1}
                        />
                      ))}
                  </Card>
                </section>
              </Card>

              <div className="mb-10 flex grow-0 flex-col justify-center gap-4 sm:flex-row">
                <RestrictedComponent requiredScopes={['campaign:delete']} behavior="hide">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteCampaign}
                    loading={deleteCampaignMutation.isPending}
                    disabled={isSubmitting}
                    type="button"
                  >
                    <Icon name="delete" color="#fff" />
                    <span>Excluir Campanha</span>
                  </Button>
                </RestrictedComponent>

                <RestrictedComponent
                  requiredScopes={['campaign:edit']}
                  behavior="disable"
                  showToast
                  toastMessage="Você não tem permissão para editar campanhas."
                >
                  <Button
                    loading={editCompaignMutation.isPending || isUploading}
                    disabled={isSubmitting}
                    type="submit"
                    className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                  >
                    <Icon name="save" color="#fff" />
                    <span className="font-['Figtree'] text-sm">Salvar</span>
                  </Button>
                </RestrictedComponent>

                {campaignToEdit?.externalId === null && (
                  <Button
                    disabled={migrateCampaignMutation.isPending}
                    className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                    onClick={(e) => {
                      e.preventDefault();
                      migrateCampaignMutation.mutate(
                        {
                          campaignUuid: campaignUuid || '',
                        },
                        {
                          onSuccess: () => {
                            toast.success('Migração realizada com sucesso');
                          },
                          onError: (error) => {
                            toast.error(`Erro ao realizar migração: ${error.message}`);
                          },
                        }
                      );
                    }}
                  >
                    <span className="font-['Figtree'] text-sm">Migrar Campanha</span>
                  </Button>
                )}
              </div>

              <CampaignDeletionModal
                isOpen={deletionModalData.isOpen}
                onClose={() => setDeletionModalData({ isOpen: false, canDelete: true })}
                onConfirm={confirmDeletion}
                canDelete={deletionModalData.canDelete}
                dependencies={deletionModalData.dependencies}
                isLoading={deleteCampaignMutation.isPending || updateAndDeleteMutation.isPending}
                availableCampaigns={availableCampaigns}
                onUpdateAndDelete={handleUpdateAndDelete}
                isLoadingCampaigns={isLoadingAvailableCampaigns}
              />
            </form>
          </Form>
        )}
      </div>
    </BaseLayout>
  );
}
